// src/components/ClassDayReminderControl.js
// ── Class-day reminder (docs/plans/release-day-classes-plan.md, R1) ──
//
// One opt-in, her own time: on mornings a new release-day class is out she gets
// a single notification at that time in her time zone — and none if she's
// already done it. Off by default; never prompted for. Lives in the class
// details (upcoming release-day class) and on the Account page.
//
// Saving goes through /api/class-day-reminder (server-written; time zone taken
// from this device). Turning it on first makes sure this device can receive
// notifications, using the same gates as the per-class "Set reminder".

import React, { useEffect, useRef, useState } from 'react';
import { isPushSupported, subscribeToPush, isSubscribedToPush, getPermissionState } from '../services/pushNotifications';

const TIMES = (() => {
    const out = [];
    for (let h = 4; h <= 21; h++) {
        for (const m of [0, 30]) out.push(`${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`);
    }
    return out;
})();

export function formatTime12(hhmm) {
    const [h, m] = (hhmm || '06:30').split(':').map(Number);
    const ap = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}

const deviceTimeZone = () => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'; } catch (_) { return 'America/Chicago'; }
};

async function api(getToken, method, body) {
    const token = await getToken();
    if (!token) throw new Error('auth');
    const r = await fetch('/api/class-day-reminder', {
        method,
        headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
}

function ClassDayReminderControl({ userId, getToken, copy = {}, variant = 'modal' }) {
    const [state, setState] = useState(null); // { enabled, time } once loaded
    const [editing, setEditing] = useState(false);
    const [time, setTime] = useState('06:30');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // getToken may be a fresh function each render — keep the latest in a ref so
    // the load runs once per member, not once per render.
    const tokenRef = useRef(getToken);
    tokenRef.current = getToken;

    useEffect(() => {
        if (!userId || typeof tokenRef.current !== 'function') return undefined;
        let alive = true;
        api(() => tokenRef.current(), 'GET').then((d) => {
            if (!alive) return;
            setState({ enabled: !!d.enabled, time: d.time || '06:30' });
            setTime(d.time || '06:30');
        }).catch(() => { if (alive) setState({ enabled: false, time: '06:30' }); });
        return () => { alive = false; };
    }, [userId]);

    if (!userId || !state) return null;

    const save = async (enabled) => {
        setBusy(true); setError('');
        try {
            if (enabled) {
                if (!isPushSupported()) {
                    setError(copy.classDayReminderNoPush || 'Reminders need notifications. On iPhone, add Moments of Movement to your home screen first.');
                    return;
                }
                if (!(await isSubscribedToPush())) {
                    const ok = await subscribeToPush(userId);
                    if (!ok) {
                        setError(getPermissionState() === 'denied'
                            ? 'Notifications are blocked for this site. Turn them on in your browser settings.'
                            : 'Tap Allow on the prompt to get your reminder.');
                        return;
                    }
                }
            }
            const d = await api(() => tokenRef.current(), 'POST', { enabled, time, timeZone: deviceTimeZone() });
            setState({ enabled: !!d.enabled, time: d.time });
            setEditing(false);
        } catch (_) {
            setError("Couldn't save that. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const isModal = variant === 'modal';
    const wrap = {
        width: '100%', boxSizing: 'border-box',
        padding: isModal ? '12px 14px' : '14px 16px', borderRadius: 12,
        background: 'var(--mom-cream)', fontFamily: 'var(--mom-font-sans)',
    };
    const label = { fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)' };
    const sub = { fontSize: 12, color: 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))', marginTop: 3, lineHeight: 1.45 };
    const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--mom-primary)' };

    if (editing) {
        return (
            <div style={wrap}>
                <div style={label}>{copy.classDayReminderPickLabel || 'Remind me at'}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select className="mom-input" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: 130 }}>
                        {TIMES.map((t) => <option key={t} value={t}>{formatTime12(t)}</option>)}
                    </select>
                    <button type="button" disabled={busy} onClick={() => save(true)}
                        style={{ padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, background: 'var(--mom-primary)', color: '#fff', opacity: busy ? 0.6 : 1 }}>
                        {busy ? 'Saving…' : (state.enabled ? 'Save' : (copy.classDayReminderOnCta || 'Turn on'))}
                    </button>
                    <button type="button" style={linkBtn} onClick={() => { setEditing(false); setError(''); setTime(state.time); }}>Cancel</button>
                </div>
                <div style={sub}>{copy.classDayReminderHint || 'One note on mornings a new class is out — never if you’ve already done it.'}</div>
                {error && <div style={{ ...sub, color: 'var(--mom-primary-dark)' }}>{error}</div>}
            </div>
        );
    }

    if (state.enabled) {
        return (
            <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px' }}>
                    <div style={label}>{(copy.classDayReminderOnLabel || 'Class-day reminder · {time}').replace('{time}', formatTime12(state.time))}</div>
                    <div style={sub}>{copy.classDayReminderOnSub || 'On mornings a new class is out.'}</div>
                </div>
                <button type="button" style={linkBtn} onClick={() => setEditing(true)}>Change</button>
                <button type="button" style={{ ...linkBtn, color: 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))' }} disabled={busy} onClick={() => save(false)}>Turn off</button>
                {error && <div style={{ ...sub, color: 'var(--mom-primary-dark)', flexBasis: '100%' }}>{error}</div>}
            </div>
        );
    }

    return (
        <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 180px' }}>
                <div style={label}>{copy.classDayReminderOffLabel || 'Want a nudge on class days?'}</div>
                <div style={sub}>{copy.classDayReminderOffSub || 'We’ll send one note at the time you pick.'}</div>
            </div>
            <button type="button" onClick={() => setEditing(true)}
                style={{ padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, fontWeight: 600, border: '1.5px solid var(--mom-primary)', background: 'transparent', color: 'var(--mom-primary)' }}>
                {copy.classDayReminderSetCta || 'Set a time'}
            </button>
        </div>
    );
}

export default ClassDayReminderControl;
